/**
 * Upgrade To Invite Modal
 *
 * Modal shown to Free/Starter users explaining team invitations require Pro plan.
 *
 * @module components/team/UpgradeToInviteModal
 */

import { useNavigate } from 'react-router-dom';
import { Modal, Button } from '@/components/ui';
import { Users, BarChart3, Shield, Headphones, ArrowRight } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface UpgradeToInviteModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function UpgradeToInviteModal({ open, onClose }: UpgradeToInviteModalProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/settings/billing');
  };

  const benefits = [
    {
      icon: <Users className="h-5 w-5" />,
      title: 'Unlimited team members',
      description: 'Collaborate and build with your team.',
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: 'Advanced analytics',
      description: 'Detailed insights into API usage and performance.',
    },
    {
      icon: <Shield className="h-5 w-5" />,
      title: 'Higher rate limits',
      description: 'Up to 250 requests per second.',
    },
    {
      icon: <Headphones className="h-5 w-5" />,
      title: 'Priority support',
      description: '24-hour or faster response times.',
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upgrade to Invite Team Members"
      description="Team collaboration is available on Pro and higher plans."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} rightIcon={<ArrowRight className="h-4 w-4" />}>
            View Plans
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-3 pt-2">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="flex items-center gap-3">
              <div className="flex-shrink-0 p-2 bg-brand-50 text-brand-600 rounded-lg">
                {benefit.icon}
              </div>
              <div>
                <p className="font-medium text-gray-900">{benefit.title}</p>
                <p className="text-sm text-gray-500">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
